// Admin "Clients" — CRM customer LIST (best-customers cockpit).
//
// One record per phone (repeat buyers unified server-side). The admin scans who
// their best customers are by lifetime revenue / profit, searches by name or
// phone, filters to repeat buyers or by acquisition source, and clicks through
// to /admin/customers/:id for the full profile.
//
// Premium cream/gold styling — KPI cards + large Fraunces money numbers, sage
// for positive profit / terracotta for negative, mobile-first (cards stack on
// phone, table-like rows on desktop), generous whitespace, subtle shadows.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customersApi, type CustomerRow } from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { ADM_COLORS as COLORS } from '@/lib/brand';
import { useT } from '@/lib/i18n';

const HEADING = "'Fraunces', 'Cormorant Garamond', Georgia, serif";
const cardStyle = {
  background: COLORS.bgCard,
  borderColor: COLORS.borderSubtle,
  boxShadow: '0 1px 3px rgba(46,36,24,0.04), 0 8px 24px rgba(46,36,24,0.05)',
} as const;

const mad = (n: number) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n || 0))} MAD`;
const moneyColor = (n: number) =>
  n > 0 ? COLORS.success : n < 0 ? COLORS.error : COLORS.textSecondary;

type SortKey = 'lifetimeRevenue' | 'netProfit';

// ─── SVG icon helper ─────────────────────────────────────────────────────────
function Icon({ d, className = 'w-[18px] h-[18px]' }: { d: React.ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {d}
    </svg>
  );
}
const ICONS = {
  people: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  repeat: <><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>,
  ltv: <><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
};

// ─── KPI card ────────────────────────────────────────────────────────────────
function KpiCard({
  icon,
  label,
  value,
  sub,
  accent = false,
  delay = 0,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  delay?: number;
}) {
  return (
    <div
      className="rounded-xl p-5 border relative overflow-hidden adm-rise transition-transform duration-200 hover:-translate-y-0.5"
      style={{ ...cardStyle, animationDelay: `${delay}ms` }}
    >
      {accent && (
        <span
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: `linear-gradient(90deg, ${COLORS.accentPrimary}, #E0BE73)` }}
        />
      )}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}
      >
        <Icon d={icon} />
      </div>
      <p className="text-[11px] font-medium uppercase tracking-wider mb-1.5" style={{ color: COLORS.textTertiary }}>
        {label}
      </p>
      <p
        className="text-[26px] leading-none mb-1"
        style={{ color: COLORS.textPrimary, fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[11px] mt-1.5" style={{ color: COLORS.textTertiary }}>
          {sub}
        </p>
      )}
    </div>
  );
}

// Small acquisition-source chip.
function SourceChip({ source }: { source: string | null }) {
  if (!source) return <span className="text-xs" style={{ color: COLORS.textTertiary }}>—</span>;
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: COLORS.bgElevated, color: COLORS.textSecondary }}
    >
      {source}
    </span>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function AdminCustomersPage() {
  const { t, lang } = useT();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('lifetimeRevenue');
  const [repeatOnly, setRepeatOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<string>('');

  const q = useQuery({ queryKey: ['customers'], queryFn: () => customersApi.list() });

  const customers = q.data?.customers ?? [];
  const stats = q.data?.stats;

  const fmtDate = (x: string | null) =>
    x
      ? new Date(x).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  // Distinct sources derived from the data.
  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const c of customers) if (c.acquisitionSource) set.add(c.acquisitionSource);
    return Array.from(set).sort();
  }, [customers]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = customers.filter((c) => {
      if (repeatOnly && !c.isRepeat) return false;
      if (sourceFilter && c.acquisitionSource !== sourceFilter) return false;
      if (term) {
        const hay = `${c.name ?? ''} ${c.phone ?? ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
    return list;
  }, [customers, search, repeatOnly, sourceFilter, sortKey]);

  // ── Sort header button ──
  function SortButton({ k, label }: { k: SortKey; label: string }) {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => setSortKey(k)}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors min-h-[44px]"
        style={
          active
            ? { background: COLORS.accentSoft, borderColor: COLORS.accentPrimary, color: COLORS.accentHover }
            : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }
        }
      >
        {label}
        {active && <span aria-hidden>↓</span>}
      </button>
    );
  }

  return (
    <AdminLayout title={t('Clients')} subtitle={t('Vos meilleurs clients — valeur à vie et profit')}>
      <div style={{ background: COLORS.bgPrimary }} className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-full">
        {/* KPI row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard
            icon={ICONS.people}
            label={t('Clients')}
            value={stats ? String(stats.totalCustomers) : '—'}
            sub={t('clients uniques (par téléphone)')}
            accent
            delay={0}
          />
          <KpiCard
            icon={ICONS.repeat}
            label={t('Taux de fidélité')}
            value={stats ? `${Math.round(stats.repeatRate)}%` : '—'}
            sub={stats ? t('{n} acheteurs récurrents', { n: stats.repeatCustomers }) : undefined}
            delay={60}
          />
          <KpiCard
            icon={ICONS.ltv}
            label={t('Valeur à vie moyenne')}
            value={stats ? mad(stats.avgLtv) : '—'}
            sub={t('revenu moyen par client')}
            delay={120}
          />
        </div>

        {/* Controls: search + sort + filters */}
        <div className="mt-5 flex flex-col gap-3">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textTertiary }}>
              <Icon d={<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>} className="w-[18px] h-[18px]" />
            </span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Rechercher par nom ou téléphone…')}
              className="w-full pl-10 pr-3 py-3 rounded-xl border text-base md:text-sm focus:outline-none focus:ring-2 bg-white min-h-[44px]"
              style={{ borderColor: COLORS.borderSubtle }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold mr-1" style={{ color: COLORS.textTertiary }}>
              {t('Trier par')}
            </span>
            <SortButton k="lifetimeRevenue" label={t('Revenu à vie')} />
            <SortButton k="netProfit" label={t('Profit net')} />

            <span className="w-px h-6 mx-1" style={{ background: COLORS.borderSubtle }} />

            <button
              type="button"
              onClick={() => setRepeatOnly((v) => !v)}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors min-h-[44px]"
              style={
                repeatOnly
                  ? { background: COLORS.accentSoft, borderColor: COLORS.accentPrimary, color: COLORS.accentHover }
                  : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }
              }
            >
              {t('Acheteurs récurrents uniquement')}
            </button>

            {sources.length > 0 && (
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-2 rounded-lg text-xs font-semibold border bg-white min-h-[44px]"
                style={{ borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }}
              >
                <option value="">{t('Toutes les sources')}</option>
                {sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* States */}
        {q.isLoading && (
          <p className="text-sm py-10 text-center" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>
        )}
        {q.isError && (
          <p className="text-sm py-10 text-center" style={{ color: COLORS.error }}>{(q.error as Error).message}</p>
        )}

        {!q.isLoading && !q.isError && (
          <div className="mt-4 rounded-xl border overflow-hidden adm-rise" style={cardStyle}>
            {/* Desktop column header */}
            <div
              className="hidden lg:grid items-center gap-3 px-5 py-3 border-b text-[11px] uppercase tracking-wider font-semibold"
              style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary, gridTemplateColumns: '2fr 1fr 0.7fr 1fr 1fr 1fr 1fr' }}
            >
              <span>{t('Client')}</span>
              <span>{t('Source')}</span>
              <span className="text-right">{t('Cmd.')}</span>
              <span className="text-right">{t('Revenu à vie')}</span>
              <span className="text-right">{t('Profit net')}</span>
              <span className="text-right">{t('Dernière commande')}</span>
              <span />
            </div>

            {rows.length === 0 && (
              <p className="text-sm p-10 text-center" style={{ color: COLORS.textTertiary }}>{t('Aucun client')}</p>
            )}

            {rows.map((c: CustomerRow) => (
              <Link
                key={c.id}
                to={`/admin/customers/${c.id}`}
                className="block px-4 sm:px-5 py-4 border-b last:border-b-0 transition-colors hover:bg-[color:var(--row-hover)]"
                style={{ borderColor: COLORS.borderSubtle, ['--row-hover' as string]: COLORS.bgElevated }}
              >
                {/* Desktop row */}
                <div
                  className="hidden lg:grid items-center gap-3"
                  style={{ gridTemplateColumns: '2fr 1fr 0.7fr 1fr 1fr 1fr 1fr' }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold truncate" style={{ color: COLORS.textPrimary }}>
                        {c.name || '—'}
                      </span>
                      {c.isRepeat && (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}
                        >
                          {t('Fidèle')}
                        </span>
                      )}
                    </div>
                    <p className="text-[12px] truncate" style={{ color: COLORS.textTertiary }}>{c.phone}</p>
                  </div>
                  <div><SourceChip source={c.acquisitionSource} /></div>
                  <span className="text-sm text-right tabular-nums" style={{ color: COLORS.textSecondary }}>{c.ordersCount}</span>
                  <span
                    className="text-right tabular-nums"
                    style={{ fontFamily: HEADING, fontWeight: 500, fontSize: 16, color: COLORS.textPrimary }}
                  >
                    {mad(c.lifetimeRevenue)}
                  </span>
                  <span
                    className="text-right tabular-nums"
                    style={{ fontFamily: HEADING, fontWeight: 500, fontSize: 16, color: moneyColor(c.netProfit) }}
                  >
                    {mad(c.netProfit)}
                  </span>
                  <span className="text-xs text-right" style={{ color: COLORS.textSecondary }}>{fmtDate(c.lastOrderAt)}</span>
                  <span className="text-right" style={{ color: COLORS.textTertiary }}>→</span>
                </div>

                {/* Mobile card */}
                <div className="lg:hidden">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold truncate" style={{ color: COLORS.textPrimary }}>
                          {c.name || '—'}
                        </span>
                        {c.isRepeat && (
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                            style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}
                          >
                            {t('Fidèle')}
                          </span>
                        )}
                      </div>
                      <p className="text-[12px] truncate" style={{ color: COLORS.textTertiary }}>{c.phone}</p>
                    </div>
                    <SourceChip source={c.acquisitionSource} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{t('Revenu à vie')}</p>
                      <p className="tabular-nums" style={{ fontFamily: HEADING, fontWeight: 500, fontSize: 18, color: COLORS.textPrimary }}>
                        {mad(c.lifetimeRevenue)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{t('Profit net')}</p>
                      <p className="tabular-nums" style={{ fontFamily: HEADING, fontWeight: 500, fontSize: 18, color: moneyColor(c.netProfit) }}>
                        {mad(c.netProfit)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px]" style={{ color: COLORS.textTertiary }}>
                    <span>{t('{n} commandes', { n: c.ordersCount })}</span>
                    <span>{fmtDate(c.lastOrderAt)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!q.isLoading && !q.isError && rows.length > 0 && (
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[11px]" style={{ color: COLORS.textTertiary }}>
              {t('{n} clients affichés', { n: rows.length })}
            </p>
            {(search || repeatOnly || sourceFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setRepeatOnly(false);
                  setSourceFilter('');
                }}
              >
                {t('Réinitialiser les filtres')}
              </Button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
